import { usePetsittingMode } from '@/store/usePetsittingMode'
export type ProfileActivityBadgeKey =
  | 'profileCard.activeToday'
  | 'profileCard.activeThisWeek'
  | 'profileCard.activeRecently'
  | 'profileCard.fastResponse'
  | 'profileCard.veryClose'
  | 'profileCard.close'
  | 'profileCard.wellRated'
  | 'profileCard.isNew'
  | 'profileCard.verified'

export type ProfileActivityBadge = {
  key: ProfileActivityBadgeKey
  defaultValue: string
}

type GetProfileActivityBadgeArgs = {
  isNew?: boolean
  lastRefresh?: string
  nowMs?: number
  reviewsCount?: number
  rating?: number
  distance?: number
  averageResponseTimeUnderThreeHours?: boolean
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000
const ONE_WEEK_MS = 7 * ONE_DAY_MS
const THREE_MONTHS_MS = 90 * ONE_DAY_MS

const BADGE_LABELS: Record<ProfileActivityBadgeKey, string> = {
  'profileCard.isNew': 'Nouveau',
  'profileCard.activeToday': "Actif aujourd'hui",
  'profileCard.activeThisWeek': 'Actif cette semaine',
  'profileCard.activeRecently': 'Actif récemment',
  'profileCard.fastResponse': 'Répond très vite',
  'profileCard.wellRated': 'Très bien noté',
  'profileCard.verified': 'Compétences vérifiées',
  'profileCard.veryClose': 'Très proche',
  'profileCard.close': 'Proche',
}

const isPetsittingMode = usePetsittingMode.getState().isPetsittingMode

export const getProfileActivityBadge = ({
  isNew = false,
  lastRefresh,
  nowMs = Date.now(),
  reviewsCount,
  rating,
  distance,
  averageResponseTimeUnderThreeHours,
}: GetProfileActivityBadgeArgs): ProfileActivityBadge | undefined => {
  if (isNew) {
    return {
      key: 'profileCard.isNew',
      defaultValue: BADGE_LABELS['profileCard.isNew'],
    }
  }

  const lastRefreshTs = lastRefresh ? new Date(lastRefresh).getTime() : NaN
  if (Number.isNaN(lastRefreshTs)) {
    return undefined
  }

  const ageMs = Math.max(0, nowMs - lastRefreshTs)

  if (ageMs < ONE_DAY_MS) {
    return {
      key: 'profileCard.activeToday',
      defaultValue: BADGE_LABELS['profileCard.activeToday'],
    }
  }

  if (ageMs < ONE_WEEK_MS) {
    return {
      key: 'profileCard.activeThisWeek',
      defaultValue: BADGE_LABELS['profileCard.activeThisWeek'],
    }
  }

  if (ageMs < THREE_MONTHS_MS) {
    return {
      key: 'profileCard.activeRecently',
      defaultValue: BADGE_LABELS['profileCard.activeRecently'],
    }
  }

  if (averageResponseTimeUnderThreeHours) {
    return {
      key: 'profileCard.fastResponse',
      defaultValue: BADGE_LABELS['profileCard.fastResponse'],
    }
  }

  if (isPetsittingMode && (reviewsCount ?? 0) > 2 && (rating ?? 0) > 4.6) {
    return {
      key: 'profileCard.wellRated',
      defaultValue: BADGE_LABELS['profileCard.wellRated'],
    }
  }

  if (isPetsittingMode) {
    return {
      key: 'profileCard.verified',
      defaultValue: BADGE_LABELS['profileCard.verified'],
    }
  }

  if (!isPetsittingMode && distance !== undefined && distance < 1) {
    return {
      key: 'profileCard.veryClose',
      defaultValue: BADGE_LABELS['profileCard.veryClose'],
    }
  }

  if (!isPetsittingMode && distance !== undefined && distance > 1 && distance < 3) {
    return {
      key: 'profileCard.close',
      defaultValue: BADGE_LABELS['profileCard.close'],
    }
  }

  return undefined
}
